import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, EyeOff, X, Save, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN } from '@/constants/testIds';

const API = '/api';
function getHeaders() { const t = localStorage.getItem('pkepl_token'); return t ? { Authorization: `Bearer ${t}` } : {}; }

const EMPTY_FORM = { title: '', client_name: '', slug: '', summary: '', content: '', industry: '', services: '', status: 'draft', seo_title: '', seo_description: '' };

function generateSlug(title) {
  return title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').trim();
}

export default function AdminCases() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchCases = () => {
    setLoading(true);
    axios.get(`${API}/admin/cases`, { withCredentials: true, headers: getHeaders() })
      .then(r => {
        const data = r.data;
        setCases(Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []));
      }).catch(() => toast.error('Failed to load cases'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCases(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditId(null); setModalOpen(true); };
  const openEdit = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/cases/${id}`, { withCredentials: true, headers: getHeaders() });
      setForm({ ...data, services: Array.isArray(data.services) ? data.services.join(', ') : (data.services || '') });
      setEditId(id); setModalOpen(true);
    } catch { toast.error('Failed to load case'); }
  };

  const handleSave = async () => {
    if (!form.title || !form.slug || !form.summary || !form.content || !form.client_name) { toast.error('All required fields must be filled.'); return; }
    setSaving(true);
    const payload = { ...form, services: form.services ? form.services.split(',').map(s => s.trim()).filter(Boolean) : [] };
    try {
      if (editId) {
        await axios.put(`${API}/admin/cases/${editId}`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Case updated');
      } else {
        await axios.post(`${API}/admin/cases`, payload, { withCredentials: true, headers: getHeaders() });
        toast.success('Case created');
      }
      setModalOpen(false); fetchCases();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/cases/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Case deleted'); setDeleteId(null); fetchCases();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleStatus = async (c) => {
    const newStatus = c.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/admin/cases/${c.id}`, { status: newStatus }, { withCredentials: true, headers: getHeaders() });
      toast.success(`Case ${newStatus}`); fetchCases();
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl font-bold text-[#153E5C]">Case Experience</h1>
          <p className="text-sm text-[#6B7280] font-sans mt-1">Manage published case studies and drafts.</p>
        </div>
        <button data-testid={ADMIN.casesNewBtn} onClick={openNew}
          className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors duration-200">
          <Plus className="w-4 h-4" /> New Case
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid={ADMIN.casesList}>
        {loading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : cases.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#6B7280] font-sans">No cases yet. Create your first case study.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full admin-table">
              <thead className="bg-[#F9FAFB]">
                <tr><th className="text-left">Title</th><th className="text-left hidden sm:table-cell">Client</th><th className="text-left hidden md:table-cell">Industry</th><th className="text-left">Status</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {cases.map(c => (
                  <tr key={c.id}>
                    <td>
                      <p className="font-medium text-[#1F2937] text-sm line-clamp-1">{c.title}</p>
                      <p className="text-xs text-[#9CA3AF]">/case-experience/{c.slug}</p>
                    </td>
                    <td className="hidden sm:table-cell text-[#6B7280] text-sm">{c.client_name}</td>
                    <td className="hidden md:table-cell text-[#6B7280] text-sm">{c.industry || '—'}</td>
                    <td>
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${c.status === 'published' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                        {c.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => toggleStatus(c)} className="p-1.5 text-[#9CA3AF] hover:text-[#0E5A57] rounded-lg hover:bg-teal-50 transition-colors duration-150">
                          {c.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => openEdit(c.id)} className="p-1.5 text-[#9CA3AF] hover:text-[#0E5A57] rounded-lg hover:bg-teal-50 transition-colors duration-150"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteId(c.id)} className="p-1.5 text-[#9CA3AF] hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors duration-150"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
              <h2 className="font-serif text-xl font-bold text-[#153E5C]">{editId ? 'Edit Case' : 'New Case'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-[#F3F4F6] text-[#6B7280]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Title *</label>
                  <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value, slug: generateSlug(e.target.value) }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="Case study title" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Client Name *</label>
                  <input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="Client / Organisation" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Slug *</label>
                  <input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="url-slug" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Industry</label>
                  <input value={form.industry} onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="Healthcare, Finance..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Status</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans bg-white focus:outline-none focus:border-[#0E5A57]">
                    <option value="draft">Draft</option><option value="published">Published</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Services (comma separated)</label>
                  <input value={form.services} onChange={e => setForm(p => ({ ...p, services: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans focus:outline-none focus:border-[#0E5A57]" placeholder="Administrative Support, Customer Support..." />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Summary *</label>
                  <textarea value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} rows={2}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-sans resize-none focus:outline-none focus:border-[#0E5A57]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5 font-sans uppercase tracking-wider">Content (HTML) *</label>
                  <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} rows={10}
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm font-mono resize-y focus:outline-none focus:border-[#0E5A57]" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E5E7EB]">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-[#6B7280] hover:text-[#374151] font-sans">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors duration-200">
                {saving ? <><Loader className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> {editId ? 'Update' : 'Create'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-xl text-center">
            <p className="font-serif text-xl font-bold text-[#153E5C] mb-3">Delete Case?</p>
            <p className="text-sm text-[#6B7280] mb-6 font-sans">This action cannot be undone.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => setDeleteId(null)} className="px-5 py-2.5 text-sm border border-[#E5E7EB] rounded-xl font-sans">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="px-5 py-2.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-xl font-sans">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
